import { Pose as MediaPipePose, Results as PoseResults } from '@mediapipe/pose'
import { Hands as MediaPipeHands, Results as HandsResults } from '@mediapipe/hands'

// NOTE: it seems to be much faster (about 50%-100% faster) to call pose & hands processors
// independently rather than using mediapipe holistic.

export default class MediapipeProcessor {
  private static isInitialized = false
  private static animationFrameRef = 0
  static mediapipeInstance: MediaPipePose
  static mediapipeHandsInstance: MediaPipeHands
  static enableEnhancedHands: boolean

  private static initializeInstance () {
    if (this.mediapipeInstance)
      return

    this.mediapipeInstance = new MediaPipePose({
      locateFile: (file: string) => {
        return `https://cdn.jsdelivr.net/npm/@mediapipe/pose@0.5.1675469404/${file}`
      },
    })

    this.mediapipeInstance.setOptions({
      modelComplexity: 0,
      smoothLandmarks: false,
      enableSegmentation: false,
      smoothSegmentation: false,
      minDetectionConfidence: 0.2,
      minTrackingConfidence: 0.8,
    })

    this.mediapipeHandsInstance = new MediaPipeHands({
      locateFile: (file: string) => {
        return `https://cdn.jsdelivr.net/npm/@mediapipe/hands@0.4.1675469240/${file}`
      },
    })

    this.mediapipeHandsInstance.setOptions({
      modelComplexity: 0,
      maxNumHands: 2,
      minDetectionConfidence: 0.8,
      minTrackingConfidence: 0.6,
    })
  }

  static async initialize () { //: Promise<[void, void]> {
    if (this.isInitialized) {
      return
    }

    this.initializeInstance()
    this.isInitialized = true

    // WARNING: mediapipe must be initialized in series, not in parallel
    // otherwise it will throw an error - so must await here
    await this.mediapipeInstance.initialize()
    await this.mediapipeHandsInstance.initialize()
  }

  static onResults (callback: (poseResults: PoseResults, handsResults: HandsResults | null) => void): void {
    this.initializeInstance()
    let lastHandResults: HandsResults | null = null
    this.mediapipeInstance.onResults((poseResults: PoseResults) => callback(poseResults, this.enableEnhancedHands ? lastHandResults : null))
    this.mediapipeHandsInstance.onResults((handResults: HandsResults) => {
      lastHandResults = handResults
    })
  }

  static startProcessing (videoElement: HTMLVideoElement): void {
    this.stopProcessing()

    const handsFrameSkip = 6
    let frame = 0

    const processFrame = async () => {
      // Check if pose instance is still valid before sending
      // TODO: refactor this
      // TODO: can mediapipe ingest video stream directly?

      // For video elements, check ready state and dimensions
      if (videoElement.readyState >= 2 && videoElement.videoWidth > 0 && videoElement.videoHeight > 0) {
        frame++

        await this.mediapipeInstance.send({ image: videoElement })
        if (this.enableEnhancedHands && frame % handsFrameSkip === 0) {
          await this.mediapipeHandsInstance.send({ image: videoElement })
        }
      }

      this.animationFrameRef = requestAnimationFrame(processFrame)
    }

    // Start the processing loop
    this.animationFrameRef = requestAnimationFrame(processFrame)
  }

  static stopProcessing (): void {
    if (this.animationFrameRef) {
      cancelAnimationFrame(this.animationFrameRef)
      this.animationFrameRef = 0
      this.mediapipeInstance.reset()
      this.mediapipeHandsInstance.reset()
    }
  }
}
